import React, { useState } from 'react';
import api from '../api/axios';
import { Mail, Lock, Save, AlertCircle, CheckCircle, User as UserIcon } from 'lucide-react';
import { useToast } from './Toast';

const EmployeeProfileSettings = ({ userInitialData }) => {
  const showToast = useToast();
  const [formData, setFormData] = useState({
    email: userInitialData?.email || '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [status, setStatus] = useState({ type: '', msg: '' });

  const handleUpdate = async (e) => {
    e.preventDefault();
    setStatus({ type: '', msg: '' });

    if (formData.newPassword && !formData.oldPassword) {
      setStatus({ type: 'error', msg: 'Veuillez saisir votre ancien mot de passe.' });
      return;
    }

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setStatus({ type: 'error', msg: 'La confirmation ne correspond pas au nouveau mot de passe.' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.put('/employee/update-profile', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast(response.data.message || 'Profil mis à jour !');
      setStatus({ type: '', msg: '' });
      setFormData({ ...formData, oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setStatus({ 
        type: 'error', 
        msg: err.response?.data?.message || 'Erreur lors de la mise à jour' 
      });
    }
  };

  return (
    <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 max-w-2xl">

      {status.msg && (
        <div className={`mb-6 p-4 rounded-2xl flex items-center font-bold text-sm ${
          status.type === 'success' 
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
            : 'bg-red-50 text-red-700 border border-red-100'
        }`}>
          {status.type === 'success' ? <CheckCircle size={18} className="mr-2" /> : <AlertCircle size={18} className="mr-2" />}
          {status.msg}
        </div>
      )}

      <form onSubmit={handleUpdate} className="space-y-6">
        
        {/* SECTION : INFORMATIONS NON MODIFIABLES */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-6 rounded-3xl border border-slate-100">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Prénom</label>
            <div className="flex items-center p-3 bg-white/50 border border-slate-200 rounded-xl text-slate-500 font-bold text-sm">
              <UserIcon size={14} className="mr-2 opacity-50" /> {userInitialData?.prenom || 'N/A'}
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Nom</label>
            <div className="flex items-center p-3 bg-white/50 border border-slate-200 rounded-xl text-slate-500 font-bold text-sm">
              {userInitialData?.nom || 'N/A'}
            </div>
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Identifiant Email</label>
            <div className="flex items-center p-3 bg-white/50 border border-slate-200 rounded-xl text-slate-500 font-bold text-sm italic">
              <Mail size={14} className="mr-2 opacity-50" /> {userInitialData?.email || 'N/A'}
            </div>
          </div>
        </div>

        <hr className="border-slate-50" />

        {/* SECTION : SÉCURITÉ (MODIFIABLE) */}
        <div className="space-y-4">
            {/* ANCIEN MOT DE PASSE */}
            <div className="space-y-2">
            <label className="text-xs font-black text-slate-400 uppercase ml-1">Mot de passe actuel</label>
            <div className="relative">
                <Lock className="absolute left-4 top-4 text-slate-300" size={20} />
                <input 
                type="password" 
                placeholder="mot de passe actuel"
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-transparent rounded-2xl outline-none focus:ring-2 ring-blue-500/20 font-medium" 
                value={formData.oldPassword} 
                onChange={e => setFormData({...formData, oldPassword: e.target.value})} 
                />
            </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* NOUVEAU MOT DE PASSE */}
                <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase ml-1">Nouveau mot de passe</label>
                    <div className="relative">
                    <Lock className="absolute left-4 top-4 text-blue-200" size={20} />
                    <input 
                        type="password" 
                        placeholder="nouveau mot de passe"
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-transparent rounded-2xl outline-none focus:ring-2 ring-blue-500/20 font-medium" 
                        value={formData.newPassword} 
                        onChange={e => setFormData({...formData, newPassword: e.target.value})} 
                    />
                    </div>
                </div>

                {/* CONFIRMATION */}
                <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase ml-1">Confirmation du mot de passe</label>
                    <div className="relative">
                    <Lock className="absolute left-4 top-4 text-blue-200" size={20} />
                    <input 
                        type="password" 
                        placeholder="confirmation du mot de passe"
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-transparent rounded-2xl outline-none focus:ring-2 ring-blue-500/20 font-medium" 
                        value={formData.confirmPassword} 
                        onChange={e => setFormData({...formData, confirmPassword: e.target.value})} 
                    />
                    </div>
                </div>
            </div>
        </div>

        <button type="submit" className="w-full p-5 bg-slate-900 text-white rounded-2xl font-black flex items-center justify-center hover:bg-black transition-all shadow-lg mt-4">
          <Save size={20} className="mr-2" /> Mettre à jour le mot de passe
        </button>
      </form>
    </div>
  );
};

export default EmployeeProfileSettings;